import { io } from "socket.io-client";

class WatchPartySocketManager {
  constructor() {
    this.socket = null;
    this.partyId = null;
    this.isConnected = false;
    this.isConnecting = false;
    /** @type {((data: { partyId?: unknown; isHost?: boolean }) => void) | null} */
    this.onJoinedRoomCallback = null;
  }

  /** Room page sets this to apply server-authoritative isHost from `joined-room`. */
  setOnJoinedRoom(callback) {
    this.onJoinedRoomCallback = callback;
  }

  connect(partyId, userId, username, avatar) {
    console.log("[SocketManager] connect() called with:", { partyId, userId, username, avatar });
    
    if (this.socket && this.isConnected) {
      console.log(
        "[WP-DEBUG] connect skipped: socket already connected (same instance; join-room not re-emitted)",
        { existingPartyId: this.partyId, requestedPartyId: partyId }
      );
      console.log("[SocketManager] Already connected to socket");
      return this.socket;
    }

    if (this.socket || this.isConnecting) {
      console.log(
        "[WP-DEBUG] connect skipped: existing socket or connection in progress",
        {
          hasSocket: !!this.socket,
          isConnected: this.isConnected,
          isConnecting: this.isConnecting,
          existingPartyId: this.partyId,
          requestedPartyId: partyId,
        }
      );
      return this.socket;
    }

    // Connect directly to backend (CORS is configured)
    const serverUrl =
      process.env.NODE_ENV === "development"
        ? "http://localhost:5000"
        : window.location.origin;

    console.log("[SocketManager] Connecting to:", serverUrl);
    this.isConnecting = true;

    try {
      this.socket = io(serverUrl, {
        path: "/socket.io/",
        transports: ["polling", "websocket"], // Try polling first, then upgrade to websocket
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        withCredentials: true, // Include credentials for CORS
      });

      console.log("[SocketManager] Socket.io client created:", this.socket);

      this.partyId = partyId;

      // Connection events
      this.socket.on("connect", () => {
        console.log("[SocketManager] Socket connected! ID:", this.socket.id);
        this.isConnected = true;
        this.isConnecting = false;

        // Join the watch party room
        console.log("[SocketManager] Emitting join-room event...");
        this.socket.emit("join-room", {
          partyId,
          userId,
          username,
          avatar,
        });
      });

      this.socket.on("disconnect", (reason) => {
        console.log("[SocketManager] Socket disconnected:", reason);
        this.isConnected = false;
        this.isConnecting = false;
      });

      this.socket.on("connect_error", (error) => {
        console.error("[SocketManager] Socket connection error:", error);
        console.error("[SocketManager] Error details:", error.message, error.type);
        this.isConnecting = false;
      });

      this.socket.on("error", (data) => {
        console.error("[SocketManager] Socket error:", data.message);
      });

      this.socket.on("joined-room", (data) => {
        console.log("[WP-DEBUG] joined-room (server isHost)", data);
        console.log("[SocketManager] Joined room successfully:", data);
        if (typeof this.onJoinedRoomCallback === "function") {
          this.onJoinedRoomCallback(data);
        }
      });

      console.log("[SocketManager] Event listeners attached");
    } catch (error) {
      console.error("[SocketManager] Failed to create socket:", error);
      this.isConnecting = false;
    }

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.isConnecting = false;
      this.partyId = null;
    }
  }

  // Host: Send play command
  emitPlay(currentTime) {
    if (!this.socket || !this.isConnected) {
      console.warn("[WP-DEBUG] emitPlay dropped", {
        hasSocket: !!this.socket,
        isConnected: this.isConnected,
        currentTime,
      });
      return;
    }
    console.log("[WP-DEBUG] emit host-play", { currentTime });
    this.socket.emit("host-play", { currentTime });
  }

  // Host: Send pause command
  emitPause(currentTime) {
    if (!this.socket || !this.isConnected) {
      console.warn("[WP-DEBUG] emitPause dropped", {
        hasSocket: !!this.socket,
        isConnected: this.isConnected,
        currentTime,
      });
      return;
    }
    console.log("[WP-DEBUG] emit host-pause", { currentTime });
    this.socket.emit("host-pause", { currentTime });
  }

  // Host: Send seek command
  emitSeek(currentTime) {
    if (!this.socket || !this.isConnected) {
      console.warn("[WP-DEBUG] emitSeek dropped", {
        hasSocket: !!this.socket,
        isConnected: this.isConnected,
        currentTime,
      });
      return;
    }
    console.log("[WP-DEBUG] emit host-seek", { currentTime });
    this.socket.emit("host-seek", { currentTime });
  }

  // Host: Send periodic heartbeat
  emitHeartbeat(currentTime, isPlaying) {
    if (!this.socket || !this.isConnected) {
      console.warn("[WP-DEBUG] emitHeartbeat dropped", {
        hasSocket: !!this.socket,
        isConnected: this.isConnected,
      });
      return;
    }
    console.log("[WP-DEBUG] emit heartbeat", { currentTime, isPlaying });
    this.socket.emit("heartbeat", { currentTime, isPlaying });
  }

  // Request current sync state
  requestSync() {
    if (!this.socket || !this.isConnected) {
      console.warn("[WP-DEBUG] requestSync dropped (not connected)");
      return;
    }
    console.log("[WP-DEBUG] emit request-sync");
    this.socket.emit("request-sync");
  }

  // Send chat message
  sendMessage(message) {
    if (!this.socket || !this.isConnected) return;
    this.socket.emit("send-message", { message });
  }

  // Host: End party
  endParty() {
    if (!this.socket || !this.isConnected) return;
    this.socket.emit("end-party");
  }

  // Listen to events
  on(event, callback) {
    if (!this.socket) return;
    this.socket.on(event, callback);
  }

  off(event, callback) {
    if (!this.socket) return;
    this.socket.off(event, callback);
  }

  // Remove all listeners for an event
  removeAllListeners(event) {
    if (!this.socket) return;
    this.socket.removeAllListeners(event);
  }
}

// Export singleton instance
const watchPartySocketManager = new WatchPartySocketManager();
export default watchPartySocketManager;